import React, { useState, useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Info,
  CheckCircle,
} from '@mui/icons-material';
import { CustomSelect, OptionType } from '../Infra/CustomSelect/CustomSelect';
import BaseModal from './BaseModal';
import { CustomButton } from '../Infra/CustomButton/CustomButton';

export interface MathOperationRule {
  formula: string; // e.g., "Column1 + 100 * Column2 - 50"
  variables: { [key: string]: string }; // Maps variable names to actual column names
}

interface MathOperationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (rule: MathOperationRule) => void;
  initialRule?: MathOperationRule;
  fieldName: string;
  availableColumns: string[];
  defaultColumn?: string | null;
}

const MathOperationModal: React.FC<MathOperationModalProps> = ({
  open,
  onClose,
  onSave,
  initialRule,
  fieldName,
  availableColumns,
  defaultColumn,
}) => {
  const [formula, setFormula] = useState<string>('');
  const [variables, setVariables] = useState<{ [key: string]: string }>({});
  const [selectedColumn, setSelectedColumn] = useState<string>('');
  const [nextVariableName, setNextVariableName] = useState<string>('A');

  useEffect(() => {
    if (initialRule) {
      setFormula(initialRule.formula);
      setVariables(initialRule.variables);
      // Calculate next variable name based on existing variables
      const existingVars = Object.keys(initialRule.variables);
      if (existingVars.length > 0) {
        const lastVar = existingVars.sort().pop();
        const nextChar = String.fromCharCode(
          (lastVar?.charCodeAt(0) || 64) + 1
        );
        setNextVariableName(nextChar);
      }
    } else {
      setFormula('');
      setVariables({});
      // Auto-add default column if provided
      if (defaultColumn) {
        setVariables({ A: defaultColumn });
        setFormula('A');
        setNextVariableName('B');
      } else {
        setNextVariableName('A');
      }
    }
  }, [initialRule, defaultColumn]);

  const columnOptions: OptionType[] = (availableColumns || []).map(col => ({
    value: col,
    label: col,
  }));

  const addColumn = () => {
    if (!selectedColumn) return;

    const varName = nextVariableName;
    setVariables(prev => ({ ...prev, [varName]: selectedColumn }));

    // Add variable to formula at cursor position or end
    setFormula(prev => prev + varName);

    // Generate next variable name
    const nextChar = String.fromCharCode(varName.charCodeAt(0) + 1);
    setNextVariableName(nextChar);
    setSelectedColumn('');
  };

  const validateFormula = (formula: string) => {
    if (!formula.trim()) {
      return { isValid: false, error: 'Please enter a formula' };
    }

    // Check for basic syntax issues
    const invalidChars = /[^A-Z0-9+\-*/().\s]/g;
    if (invalidChars.test(formula)) {
      return { isValid: false, error: 'Formula contains invalid characters' };
    }

    // Check for balanced parentheses
    let openParens = 0;
    for (const char of formula) {
      if (char === '(') openParens++;
      if (char === ')') openParens--;
      if (openParens < 0) {
        return { isValid: false, error: 'Unmatched closing parenthesis' };
      }
    }
    if (openParens > 0) {
      return { isValid: false, error: 'Unmatched opening parenthesis' };
    }

    return { isValid: true, error: null };
  };

  const handleSave = () => {
    const validation = validateFormula(formula);
    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    const rule: MathOperationRule = {
      formula: formula.trim(),
      variables,
    };

    onSave(rule);
    onClose();
  };

  const formulaValidation = validateFormula(formula);
  const hasValidFormula =
    formulaValidation.isValid && Object.keys(variables).length > 0;

  const getFormulaPreview = () => {
    let preview = formula;
    Object.entries(variables).forEach(([varName, columnName]) => {
      preview = preview.replace(
        new RegExp(`\\b${varName}\\b`, 'g'),
        `[${columnName}]`
      );
    });
    return preview || 'No formula entered';
  };

  const removeVariable = (varName: string) => {
    const newVariables = { ...variables };
    delete newVariables[varName];
    setVariables(newVariables);

    // Remove variable from formula
    const regex = new RegExp(`\\b${varName}\\b`, 'g');
    setFormula(prev => prev.replace(regex, ''));
  };

  return (
    <BaseModal
      dataTestId="math-operation-modal"
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title={`Math Operation - ${fieldName}`}
      icon={<CalculateIcon />}
      submitText={
        hasValidFormula ? 'Save Formula' : 'Enter Formula & Variables'
      }
      submitDisabled={!hasValidFormula}
      maxWidth="sm"
    >
      <Stack spacing={3}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Why use mathematical operations?
          </Typography>
          <Typography variant="body2">
            Create formulas to transform column values using math operations (+,
            -, *, /, parentheses). Variables (A, B, C...) represent your Excel
            columns, and you can combine them with numbers.
          </Typography>
        </Alert>

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalculateIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Step 1: Map Columns to Variables
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select Excel columns and assign them to variables (A, B, C...) that
            you'll use in your formula.
          </Typography>

          <Paper sx={{ p: 3, borderRadius: 2 }} variant="outlined">
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, color: 'text.secondary' }}
            >
              Map Column to Variable
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
              <CustomSelect
                placeholder="Select column"
                options={columnOptions.filter(
                  opt => !Object.values(variables).includes(opt.value as string)
                )}
                value={selectedColumn}
                onChange={value => setSelectedColumn(value as string)}
                size="sm"
              />
              <Typography variant="body2" sx={{ mx: 1 }}>
                →
              </Typography>
              <Chip
                label={nextVariableName}
                size="small"
                sx={{
                  bgcolor: selectedColumn ? 'primary.50' : 'grey.100',
                  color: selectedColumn ? 'primary.main' : 'grey.600',
                  borderColor: selectedColumn ? 'primary.200' : 'grey.300',
                  fontWeight: 'bold',
                  minWidth: 30,
                }}
                variant="outlined"
              />
              <CustomButton
                variant="contained"
                onClick={addColumn}
                disabled={
                  !selectedColumn ||
                  columnOptions.filter(
                    opt =>
                      !Object.values(variables).includes(opt.value as string)
                  ).length === 0
                }
                size="small"
              >
                Add Variable
              </CustomButton>
            </Box>
            {selectedColumn && (
              <Typography
                variant="caption"
                color="success.main"
                sx={{ display: 'block', mb: 1 }}
              >
                ✓ Will create variable {nextVariableName} = {selectedColumn}
              </Typography>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Next variable:</strong> {nextVariableName} |
                <strong>Available columns:</strong>{' '}
                {
                  columnOptions.filter(
                    opt =>
                      !Object.values(variables).includes(opt.value as string)
                  ).length
                }{' '}
                remaining
                {columnOptions.filter(
                  opt => !Object.values(variables).includes(opt.value as string)
                ).length === 0 && ' (all columns have been assigned)'}
              </Typography>
            </Alert>
          </Paper>
        </Paper>

        {Object.keys(variables).length > 0 ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircle color="success" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Current Variables ({Object.keys(variables).length})
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These variables are available for use in your formula.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              {Object.entries(variables).map(([varName, columnName]) => (
                <Chip
                  key={varName}
                  label={`${varName} = ${columnName}`}
                  onDelete={() => removeVariable(varName)}
                  sx={{
                    bgcolor: 'grey.100',
                    color: 'grey.800',
                    borderColor: 'grey.300',
                    '&:hover': {
                      bgcolor: 'grey.200',
                    },
                    '& .MuiChip-deleteIcon': {
                      color: 'grey.600',
                      '&:hover': {
                        color: 'grey.800',
                      },
                    },
                  }}
                  variant="outlined"
                />
              ))}
            </Stack>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Info color="action" />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                No Variables Yet
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Add variables above to start building your formula. Each variable
              represents a column from your Excel file.
            </Typography>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Info color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Step 2: Create Your Formula
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use your defined variables and mathematical operators (+, -, *, /,
            parentheses) to create your formula.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Mathematical Formula"
            placeholder={
              Object.keys(variables).length > 0
                ? `Enter your formula using variables: ${Object.keys(variables).join(', ')}`
                : 'First add variables above, then create your formula'
            }
            value={formula}
            onChange={e => setFormula(e.target.value)}
            disabled={Object.keys(variables).length === 0}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '16px',
              },
            }}
            helperText={
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Supported operators:</strong> + (add), - (subtract), *
                  (multiply), / (divide), ( ) (parentheses)
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  <strong>Examples:</strong> A + 100, A * B - 50, (A + B) / 2, A
                  * 1.5 + 200
                </Typography>
              </Box>
            }
          />
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 2, bgcolor: 'primary.50' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CalculateIcon color="primary" />
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'primary.main' }}
            >
              Formula Preview
            </Typography>
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, color: 'text.secondary' }}
          >
            How your formula will look with actual column names:
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                color:
                  getFormulaPreview() === 'No formula entered'
                    ? 'text.secondary'
                    : 'text.primary',
                fontStyle:
                  getFormulaPreview() === 'No formula entered'
                    ? 'italic'
                    : 'normal',
              }}
            >
              {getFormulaPreview()}
            </Typography>
          </Box>
          {formula && Object.keys(variables).length > 0 && (
            <Alert severity="success" sx={{ fontSize: '0.875rem' }}>
              <Typography variant="caption">
                ✓ Formula uses {Object.keys(variables).length} variable(s) and
                is ready to calculate
              </Typography>
            </Alert>
          )}
          {!formulaValidation.isValid && formula && (
            <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
              <Typography variant="caption">
                ✗ {formulaValidation.error}
              </Typography>
            </Alert>
          )}
          {formulaValidation.isValid &&
            formula &&
            Object.keys(variables).length === 0 && (
              <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                <Typography variant="caption">
                  ⚠ Formula is valid but no variables are defined
                </Typography>
              </Alert>
            )}
        </Paper>
      </Stack>
    </BaseModal>
  );
};

export default MathOperationModal;
